import { useCallback, useEffect, useRef, useState } from 'react';
import { parseEther } from 'ethers/lib/utils';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components/macro';

import { H2, Label3, Value5 } from '../Text';
import { MarketSelector as BaseMarketSelector } from './MarketSelector';
import useLayout from '../../contexts/LayoutContext';
import { MarketInfo } from '../../utils/helpers';
import { Tooltip } from '../Tooltip';
import arrow from '../../assets/vectors/arrow-s.svg';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding: 0.25rem;
  margin-bottom: 0.75rem;
  overflow: hidden;
  height: 12.5rem;
  margin-bottom: -8rem;

  @media only screen and (max-width: ${({ theme }) => theme.tablet}) {
    justify-content: space-between;
  }
`;

const MarketPrice = styled(H2)`
  height: 4rem;
  z-index: 2;
  padding-left: 1.5rem;
  display: flex;
  align-items: center;
  margin-right: -1rem;
  padding-right: 1rem;
  background: ${({ theme }) => theme.bg2};

  span {
    transition-property: color;
    transition-duration: 300ms;

    &.red {
      transition-duration: 0ms;
      color: ${({ theme }) => theme.red};
    }
    &.green {
      transition-duration: 0ms;
      color: ${({ theme }) => theme.green};
    }
  }

  @media only screen and (max-width: ${({ theme }) => theme.tablet}) {
    margin-bottom: -2rem;
  }
}
`;

const StatWrapper = styled.div`
  margin: 0rem 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  height: 4rem;
  background: ${({ theme }) => theme.bg2};

  @media only screen and (max-width: ${({ theme }) => theme.tablet}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    height: 2.4rem;
    margin: 0rem 1rem;
  }
`;

const LabelWithTooltip = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

const TooltipWrapper = styled.div`
  margin-bottom: 0.25rem;
`;

const StatsContainer = styled.div`
  display: flex;
  position: relative;
  flex-direction: row;
  justify-content: flex-start;
  overflow-x: scroll;
  overflow-y: visible;
  padding: 0 10rem;
  margin: 0 -9.5rem;
`;

const OverflowHintLeft = styled.div<{ scrollPosition: number }>`
  background: linear-gradient(90deg, ${({ theme }) => theme.bg2} 50%, #00000000 100%);
  z-index: 2;
  width: 4rem;
  height: 4rem;
  margin-right: -1rem;
  pointer-events: none;
  display: flex;
  align-items: center;

  &:after {
    display: block;
    opacity: ${({ scrollPosition }) => (scrollPosition === 0 ? '0' : '1')};
    content: url(${arrow});
    transform: rotate(90deg) translateY(0.75rem);
  }
`;

const OverflowHintRight = styled.div<{ scrollPosition: number }>`
  background: linear-gradient(90deg, #00000000 0%, ${({ theme }) => theme.bg2} 100%);
  z-index: 2;
  width: 2rem;
  margin-left: -1.25rem;
  margin-right: -0.25rem;
  padding-right: 1.25rem;
  height: 4rem;
  pointer-events: none;
  display: flex;
  align-items: center;

  &:after {
    display: block;
    opacity: ${({ scrollPosition }) => (scrollPosition > 0.99 ? '0' : '1')};
    content: url(${arrow});
    transform: rotate(-90deg) translateY(1.25rem);
  }
`;

const StatLabel = styled(Label3)`
  margin-top: 0;
  margin-bottom: 0.25rem;
  white-space: nowrap;

  @media only screen and (max-width: ${({ theme }) => theme.tablet}) {
    flex: 1;
  }
`;

const StatVal = styled(Value5)`
  margin: 0;
  white-space: nowrap;

  @media only screen and (max-width: ${({ theme }) => theme.tablet}) {
    flex: 1;
    text-align: right;
  }
`;

const ChartContainer = styled.div`
  flex: 1;
  width: 100%;
  position: relative;
  border-top: 0.066rem solid ${({ theme }) => theme.bg5};
  border-bottom-right-radius: 0.75rem;
  border-bottom-left-radius: 0.75rem;
  overflow: hidden;

  @media only screen and (max-width: ${({ theme }) => theme.tablet}) {
    min-height: 50vh;
  }
`;

const FooterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: 0.75rem;
`;

const FooterCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;
const MarketSelector = styled(BaseMarketSelector)`
  height: 4rem;
`;

export interface MarketHeaderProps extends MarketInfo {
  children: React.ReactNode;
  price?: string;
  priceChangeVal?: number;
  priceChange?: string;
  mark?: string;
  index?: string;
  fundingRateVal?: number;
  fundingRate?: string;
  vol?: string;
  openInterest?: string;
  liquidity?: string;
}

export function MarketContainer({
  children,
  price,
  priceChangeVal,
  priceChange,
  mark,
  index,
  fundingRateVal,
  fundingRate,
  vol,
  openInterest,
  liquidity,
}: MarketHeaderProps) {
  const theme = useTheme();
  const { isMobile, isTablet, screenWidth } = useLayout();
  const { t } = useTranslation();

  const marketPriceElem = useRef<HTMLSpanElement>(null);
  const scrollContainer = useRef<HTMLDivElement>(null);

  const [, setLastPriceValue] = useState<string>();
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const [overflowing, setOverflowing] = useState(false);

  const handleHeaderScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    // eslint-disable-next-line
    const { scrollLeft, clientWidth, scrollWidth } = e.nativeEvent.target as any;
    setScrollPosition(scrollLeft / (scrollWidth - clientWidth));
  };

  // Add and remove color className
  const flashPrice = useCallback(
    (color: 'red' | 'green') => {
      if (marketPriceElem?.current) {
        marketPriceElem.current.classList.add(color);

        setTimeout(() => {
          if (marketPriceElem?.current) {
            marketPriceElem.current.classList.remove(color);
          }
        }, 250);
      }
    },
    [marketPriceElem],
  );

  // Flash market price green or red on change
  useEffect(() => {
    if (price !== undefined && price !== '-') {
      setLastPriceValue((last?: string) => {
        if (last === undefined) {
          return price;
        }
        if (parseEther(price).gt(parseEther(last))) {
          flashPrice('green');
          return price;
        }
        if (parseEther(price).lt(parseEther(last))) {
          flashPrice('red');
          return price;
        }
        return undefined;
      });
    }
  }, [price, theme, flashPrice]);

  useEffect(() => {
    if (scrollContainer.current) {
      const el = scrollContainer.current;
      setOverflowing(el.clientWidth < el.scrollWidth);
    }
  }, [
    setOverflowing,
    scrollContainer,
    screenWidth,
    scrollPosition,
    price,
    priceChange,
    mark,
    index,
    fundingRate,
    openInterest,
    liquidity,
    vol,
  ]);

  return (
    <Container>
      <HeaderWrapper>
        <MarketSelector />
        <StatWrapper>
          <MarketPrice>
            <span ref={marketPriceElem}>{price}</span>
          </MarketPrice>
        </StatWrapper>
        {!isMobile && !isTablet && (
          <>
            {overflowing && <OverflowHintLeft scrollPosition={scrollPosition} />}
            <StatsContainer ref={scrollContainer} onScroll={handleHeaderScroll}>
              <StatWrapper>
                <StatLabel>{t('24h change')}</StatLabel>
                <StatVal value={priceChangeVal}>{priceChange}</StatVal>
              </StatWrapper>
              <StatWrapper>
                <StatLabel>{t('Mark')}</StatLabel>
                <StatVal>{mark}</StatVal>
              </StatWrapper>
              <StatWrapper>
                <StatLabel>{t('Index')}</StatLabel>
                <StatVal>{index}</StatVal>
              </StatWrapper>
              <StatWrapper>
                <LabelWithTooltip>
                  <StatLabel>{t('24h funding rate')}</StatLabel>
                  <TooltipWrapper>
                    <Tooltip size="s" position="bottom" offset={0}>
                      {t(
                        'The funding rate encourages the market price to converge with index price. Longs pay shorts when funding is positive and vice versa.',
                      )}
                    </Tooltip>
                  </TooltipWrapper>
                </LabelWithTooltip>
                <StatVal value={fundingRateVal}>{fundingRate}</StatVal>
              </StatWrapper>
              <StatWrapper>
                <StatLabel>{t('Open interest')}</StatLabel>
                <StatVal>{openInterest}</StatVal>
              </StatWrapper>
              <StatWrapper>
                <StatLabel>{t('24h volume')}</StatLabel>
                <StatVal>{vol}</StatVal>
              </StatWrapper>
              <StatWrapper>
                <StatLabel>{t('Liquidity')}</StatLabel>
                <StatVal>{liquidity}</StatVal>
              </StatWrapper>
            </StatsContainer>
            {overflowing && <OverflowHintRight scrollPosition={scrollPosition} />}
          </>
        )}
      </HeaderWrapper>
      <ChartContainer>{children}</ChartContainer>
      {(isMobile || isTablet) && (
        <FooterWrapper>
          <FooterCol>
            <StatWrapper>
              <StatLabel>{t('24h change')}</StatLabel>
              <StatVal value={priceChangeVal}>{priceChange}</StatVal>
            </StatWrapper>
            <StatWrapper>
              <StatLabel>{t('24h volume')}</StatLabel>
              <StatVal>{vol}</StatVal>
            </StatWrapper>
          </FooterCol>
          <FooterCol>
            <StatWrapper>
              <StatLabel>{t('Mark')}</StatLabel>
              <StatVal>{mark}</StatVal>
            </StatWrapper>
            <StatWrapper>
              <StatLabel>{t('Index')}</StatLabel>
              <StatVal>{index}</StatVal>
            </StatWrapper>
            <StatWrapper>
              <StatLabel>{t('Funding rate')}</StatLabel>
              <StatVal value={fundingRateVal}>{fundingRate}</StatVal>
            </StatWrapper>
            <StatWrapper>
              <StatLabel>{t('Open interest')}</StatLabel>
              <StatVal>{openInterest}</StatVal>
            </StatWrapper>
            <StatWrapper>
              <StatLabel>{t('Liquidity')}</StatLabel>
              <StatVal>{liquidity}</StatVal>
            </StatWrapper>
          </FooterCol>
        </FooterWrapper>
      )}
    </Container>
  );
}

MarketContainer.defaultProps = {
  priceChangeVal: 0,
  priceChange: '-',
  price: '-',
  mark: '-',
  index: '-',
  fundingRateVal: 0,
  fundingRate: '-',
  vol: '-',
  openInterest: '-',
  liquidity: '-',
};
